import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle2, Loader2, ShieldCheck, Music2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public split-sheet signing page.  No login required — token in URL is proof of identity.
 */
export default function SplitSheetSign() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [signature, setSignature] = useState("");
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/split-sheets/sign/${token}`);
        setState({ loading: false, error: null, data });
        if (data.your_slot?.signed_at) setSigned(true);
      } catch (e) {
        setState({ loading: false, error: e.response?.data?.detail || "Link invalid or expired.", data: null });
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!agree) { toast.error("You must agree to sign."); return; }
    if (signature.trim().length < 2) { toast.error("Type your full legal name to sign."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/split-sheets/sign/${token}`, { signature, agree });
      setSigned(true);
      toast.success("Signature recorded.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signature failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="text-[var(--primary)] animate-spin" size={28} /></div>;
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="sign-error">
        <div className="text-center max-w-md">
          <h1 className="font-heading font-black text-3xl mb-3">Link invalid.</h1>
          <p className="text-zinc-400">{state.error}</p>
        </div>
      </div>
    );
  }
  const d = state.data;
  const slot = d.your_slot;

  return (
    <div className="min-h-screen bg-[#050505] text-white py-14 px-6" data-testid="sign-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Music2 className="text-[var(--primary)]" size={22} />
          <div className="font-heading font-black text-xl tracking-tight">E-QUANTUM CONSULTS</div>
        </div>

        <div className="border border-white/10 bg-[#0a0a0a] p-6 md:p-10">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--primary)] mb-3">
            // Split Sheet — signature required
          </div>
          <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tighter leading-[0.95] mb-4">
            &ldquo;{d.song_title}&rdquo;
          </h1>
          <p className="text-zinc-400 text-sm mb-1">
            Requested by <strong className="text-white">{d.owner_email}</strong>
            {d.date_written && <> · Written {d.date_written}</>}
            {d.session_location && <> · {d.session_location}</>}
          </p>

          {/* Your slot */}
          <div className="mt-8 border border-[var(--primary)]/40 bg-[var(--primary)]/5 p-5">
            <div className="font-mono text-[10px] tracking-widest uppercase text-[var(--primary)] mb-2">Your share</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><Lbl>Name</Lbl><div className="text-white">{slot.name}</div></div>
              <div><Lbl>Role</Lbl><div className="text-white">{slot.role}</div></div>
              <div><Lbl>Split</Lbl><div className="text-[var(--primary)] font-heading font-black text-2xl">{slot.split_pct}%</div></div>
              <div><Lbl>PRO</Lbl><div className="text-white">{slot.pro || "—"}</div></div>
            </div>
          </div>

          {/* Full breakdown */}
          <div className="mt-6">
            <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-3">Full ownership breakdown</div>
            <div className="border border-white/10 divide-y divide-white/5">
              {d.collaborators.map((c, i) => (
                <div key={c.email || `collab-${i}`} className="grid grid-cols-12 gap-3 p-3 text-sm items-center">
                  <div className="col-span-5 md:col-span-4">
                    <div className="text-white font-medium">{c.name}</div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{c.role} · {c.pro || "—"}</div>
                  </div>
                  <div className="col-span-3 md:col-span-3 font-mono text-[10px] text-zinc-400 truncate">{c.email}</div>
                  <div className="col-span-2 md:col-span-3 font-mono text-[10px] text-zinc-500 truncate">{c.publisher || "—"}</div>
                  <div className="col-span-2 md:col-span-2 text-right">
                    <span className="font-heading font-black text-lg text-[var(--primary)]">{c.split_pct}%</span>
                    {c.signed_at && (
                      <div className="font-mono text-[9px] tracking-widest uppercase text-[#22c55e] mt-1 inline-flex items-center gap-1">
                        <CheckCircle2 size={10} /> Signed
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sign */}
          {signed ? (
            <div className="mt-10 text-center border border-[var(--primary)]/50 bg-[var(--primary)]/5 p-8" data-testid="signed-state">
              <CheckCircle2 size={40} className="text-[var(--primary)] mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="font-heading font-black text-2xl mb-2">Your signature is recorded.</h2>
              <p className="text-zinc-400 text-sm">The song owner will be notified. You&rsquo;ll receive a copy of the fully-executed PDF once everyone signs.</p>
            </div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-10 space-y-5" data-testid="sign-form">
              <div className="border border-white/10 p-5 text-sm text-zinc-300">
                <ShieldCheck className="text-[var(--primary)] mb-2" size={18} />
                By typing your name and clicking Sign below, you agree that the ownership splits above are accurate,
                and authorize this split sheet to be filed with any relevant rights organization.
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 w-4 h-4 accent-[var(--primary)]" data-testid="sign-agree" />
                <span className="text-sm text-zinc-200">I agree these splits are accurate.</span>
              </label>
              <div>
                <Lbl>Type your full legal name to sign</Lbl>
                <input value={signature} onChange={(e) => setSignature(e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[var(--primary)] outline-none py-3 text-white text-lg" placeholder={slot.name} data-testid="sign-signature" />
              </div>
              <button onClick={submit} disabled={submitting} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="sign-submit">
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Signing…</> : <>Sign the split sheet</>}
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
function Lbl({ children }) {
  return <label className="block font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-1">{children}</label>;
}
